import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { useAuth } from '../../context/AuthContext';
import BranchSelectModal from '../../components/common/BranchSelectModal';
import PackageOptionPickerModal from '../../components/common/PackageOptionPickerModal';
import TypeSearch from '../../components/common/TypeSearch';

const EMPTY_FLIGHT = { airline: '', flight_no: '', date: '', from: '', to: '', dep: '', arr: '' };
const EMPTY_DAY = (n) => ({ day: n, title: '', activities: '' });
const PKG_PRICE_TYPES = ['Double Sharing', 'Single Occupancy', 'Child With Bed', 'Child No Bed'];

let optionSeq = 0;
// A fresh option always starts with blank pax rates. Everything else (tour details,
// flights, itinerary, inclusions/exclusions) is copied from `base` (the option it was
// added from) so staff are tweaking a near-duplicate rather than starting from scratch.
function makeOption(label, base = null) {
  return {
    key: `opt-${++optionSeq}`,
    label,
    detailMeta: base ? { ...base.detailMeta } : { travel_period: '', book_before: '', min_pax_note: '' },
    paxPricing: PKG_PRICE_TYPES.map(t => ({ type: t, rate: '' })),
    flights: base ? base.flights.map(f => ({ ...f })) : [{ ...EMPTY_FLIGHT }],
    itinerary: base ? base.itinerary.map(d => ({ ...d })) : [EMPTY_DAY(1)],
    inclusions: base ? [...base.inclusions] : [''],
    exclusions: base ? [...base.exclusions] : [''],
    templateInclusions: base ? base.templateInclusions.map(it => ({ ...it })) : [],
    templateExclusions: base ? base.templateExclusions.map(it => ({ ...it })) : [],
  };
}

export default function SaleQuotationForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, branchId } = useAuth();
  const username = user?.email?.split('@')[0] || 'unknown';
  const isEdit = !!id;

  const [form, setForm] = useState({
    title: '', destination: '', duration: '',
    per_pax_rate: '', currency_code: 'LKR',
    min_pax: '', max_pax: '', valid_until: '', advance_percent: '', advance_amount: '', notes: '', status: 'DRAFT',
    quotation_type: 'simple',
    agent_ref_id: '', agent_ref_label: '',
  });
  // Simple mode only — Detail Package uses per-option inclusions/exclusions instead.
  const [inclusions, setInclusions] = useState(['']);

  // Detail Package: one or more options (Option 1, Option 2, …), each a full copy of
  // tour details / pricing / inclusions / exclusions / flights / itinerary.
  const [options, setOptions] = useState([makeOption('Option 1')]);
  const [packageTemplates, setPackageTemplates] = useState([]);
  const [templatePickerFor, setTemplatePickerFor] = useState(null); // option key, or null

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [branchConfirmed, setBranchConfirmed] = useState(false);

  useEffect(() => {
    supabase.from('branches').select('id, name, code').eq('is_active', true).order('name').then(({ data }) => setBranches(data || []));
  }, []);
  useEffect(() => { if (branchId && !selectedBranchId) setSelectedBranchId(branchId); }, [branchId]);

  useEffect(() => {
    supabase.from('package_option_templates').select('*').eq('is_active', true).order('name')
      .then(({ data }) => setPackageTemplates(data || []));
  }, []);

  useEffect(() => {
    if (isEdit) {
      supabase.from('sale_quotations').select('*, agents:agent_ref_id(tls_code, full_name)').eq('id', id).single().then(({ data }) => {
        if (data) {
          setForm({
            title: data.title || '', destination: data.destination || '',
            duration: data.duration || '', per_pax_rate: data.per_pax_rate || '',
            currency_code: data.currency_code || 'LKR', min_pax: data.min_pax || '',
            max_pax: data.max_pax || '', valid_until: data.valid_until || '',
            advance_percent: data.advance_percent != null ? String(data.advance_percent) : '',
            advance_amount: data.advance_amount != null ? String(data.advance_amount) : '',
            notes: data.notes || '', status: data.status || 'DRAFT',
            quotation_type: data.quotation_type || 'simple',
            agent_ref_id: data.agent_ref_id || '',
            agent_ref_label: data.agents ? `${data.agents.tls_code} — ${data.agents.full_name}` : '',
          });
          setInclusions(data.inclusions?.length ? data.inclusions : ['']);
          const dd = data.detail_data || {};
          if (dd.options?.length) {
            setOptions(dd.options.map((o, i) => ({
              key: `opt-${++optionSeq}`,
              label: o.label || `Option ${i + 1}`,
              detailMeta: { travel_period: o.travel_period || '', book_before: o.book_before || '', min_pax_note: o.min_pax_note || '' },
              paxPricing: o.pax_pricing?.length ? o.pax_pricing : PKG_PRICE_TYPES.map(t => ({ type: t, rate: '' })),
              flights: o.flights?.length ? o.flights : [{ ...EMPTY_FLIGHT }],
              itinerary: o.itinerary?.length
                ? o.itinerary.map(d => ({ ...d, activities: Array.isArray(d.activities) ? d.activities.join('\n') : (d.activities || '') }))
                : [EMPTY_DAY(1)],
              // Already-saved inclusions/exclusions are a flat merged list — load them as
              // free text; re-loading a Package Option template starts a fresh checklist.
              inclusions: o.inclusions?.length ? o.inclusions : [''],
              exclusions: o.exclusions?.length ? o.exclusions : [''],
              templateInclusions: [],
              templateExclusions: [],
            })));
          } else if (data.quotation_type === 'detail_package' && (dd.pax_pricing || dd.flights || dd.itinerary || data.inclusions?.length || data.exclusions?.length)) {
            // Legacy single-option detail package saved before multi-option support.
            setOptions([{
              key: `opt-${++optionSeq}`,
              label: 'Option 1',
              detailMeta: { travel_period: dd.travel_period || '', book_before: dd.book_before || '', min_pax_note: dd.min_pax_note || '' },
              paxPricing: dd.pax_pricing?.length ? dd.pax_pricing : PKG_PRICE_TYPES.map(t => ({ type: t, rate: '' })),
              flights: dd.flights?.length ? dd.flights : [{ ...EMPTY_FLIGHT }],
              itinerary: dd.itinerary?.length
                ? dd.itinerary.map(d => ({ ...d, activities: Array.isArray(d.activities) ? d.activities.join('\n') : (d.activities || '') }))
                : [EMPTY_DAY(1)],
              inclusions: data.inclusions?.length ? data.inclusions : [''],
              exclusions: data.exclusions?.length ? data.exclusions : [''],
              templateInclusions: [],
              templateExclusions: [],
            }]);
          }
        }
      });
    }
  }, [id, isEdit]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleTypeChange = (newType) => set('quotation_type', newType);

  const updateOption = (key, fields) => setOptions(opts => opts.map(o => o.key === key ? { ...o, ...fields } : o));
  const addOption = () => setOptions(opts => [...opts, makeOption(`Option ${opts.length + 1}`, opts[opts.length - 1])]);
  const removeOption = (key) => setOptions(opts => opts.length > 1 ? opts.filter(o => o.key !== key) : opts);

  const selectPackageTemplate = (template) => {
    updateOption(templatePickerFor, {
      templateInclusions: (template.inclusions || []).map(label => ({ label, checked: false })),
      templateExclusions: (template.exclusions || []).map(label => ({ label, checked: false })),
    });
    setTemplatePickerFor(null);
  };

  const toggleTemplateItem = (optKey, kind, i) => {
    setOptions(opts => opts.map(o => {
      if (o.key !== optKey) return o;
      const items = o[kind].map((it, idx) => idx === i ? { ...it, checked: !it.checked } : it);
      return { ...o, [kind]: items };
    }));
  };

  async function handleSave() {
    if (!form.title) { setError('Title is required.'); return; }
    setSaving(true); setError('');
    try {
      const isDetail = form.quotation_type === 'detail_package';
      let detail_data = null;
      let topInclusions = inclusions.filter(x => x.trim());
      let topExclusions = [];

      if (isDetail) {
        const cleanedOptions = options.map(o => {
          const cleanInclusions = [
            ...o.templateInclusions.filter(it => it.checked).map(it => it.label),
            ...o.inclusions.filter(x => x.trim()),
          ];
          const cleanExclusions = [
            ...o.templateExclusions.filter(it => it.checked).map(it => it.label),
            ...o.exclusions.filter(x => x.trim()),
          ];
          return {
            label: o.label,
            ...o.detailMeta,
            pax_pricing: o.paxPricing.filter(p => p.type || p.rate),
            flights: o.flights.filter(f => f.airline || f.flight_no),
            itinerary: o.itinerary
              .filter(d => d.title || d.activities.trim())
              .map(d => ({ ...d, activities: d.activities.split('\n').map(s => s.trim()).filter(Boolean) })),
            inclusions: cleanInclusions,
            exclusions: cleanExclusions,
          };
        });
        detail_data = { options: cleanedOptions };
        // Top-level inclusions/exclusions mirror the first option, kept for anything
        // elsewhere that reads them directly (e.g. list previews) — the real per-option
        // data lives in detail_data.options.
        topInclusions = cleanedOptions[0]?.inclusions || [];
        topExclusions = cleanedOptions[0]?.exclusions || [];
      }

      const { agent_ref_label, ...formNoLabel } = form;
      const sanitized = {
        ...formNoLabel,
        min_pax: parseInt(form.min_pax) || null,
        max_pax: parseInt(form.max_pax) || null,
        per_pax_rate: parseFloat(form.per_pax_rate) || null,
        advance_percent: parseFloat(form.advance_percent) || null,
        advance_amount: parseFloat(form.advance_amount) || null,
        agent_ref_id: form.agent_ref_id || null,
      };

      if (isEdit) {
        const { error: e } = await supabase.from('sale_quotations').update({
          ...sanitized, inclusions: topInclusions, exclusions: topExclusions, detail_data, updated_at: new Date().toISOString(),
        }).eq('id', id);
        if (e) throw e;
        navigate(`/sale-quotations/${id}`);
      } else {
        const yy = String(new Date().getFullYear()).slice(-2);
        const prefix = `QT-${yy}`;
        const { data: existing } = await supabase.from('sale_quotations').select('quotation_number').ilike('quotation_number', `${prefix}-%`);
        const maxSeq = (existing || []).reduce((max, r) => {
          const seq = parseInt((r.quotation_number || '').split('-').pop()) || 0;
          return seq > max ? seq : max;
        }, 0);
        const quotation_number = `${prefix}-${String(maxSeq + 1).padStart(4, '0')}`;
        const { data, error: e } = await supabase.from('sale_quotations').insert({
          ...sanitized, inclusions: topInclusions, exclusions: topExclusions, detail_data, quotation_number, created_by: username, branch_id: selectedBranchId || null,
        }).select().single();
        if (e) throw e;
        navigate(`/sale-quotations/${data.id}`);
      }
    } catch (e) { setError(e.message || 'Error saving'); setSaving(false); }
  }

  const isDetail = form.quotation_type === 'detail_package';

  // Advance basis: the best-known "total" for this quote. Simple mode = per-pax rate ×
  // min pax; Detail mode = Option 1's first filled pax rate × min pax. Quotes are
  // per-pax pitches without a firm invoice total, so this is an estimate for convenience.
  const advanceBaseRate = isDetail
    ? parseFloat((options[0]?.paxPricing || []).find(p => parseFloat(p.rate) > 0)?.rate || 0)
    : parseFloat(form.per_pax_rate) || 0;
  const advanceBaseTotal = advanceBaseRate * (parseInt(form.min_pax) || 1);

  const setAdvancePercent = (v) => {
    setForm(f => ({
      ...f,
      advance_percent: v,
      advance_amount: v && advanceBaseTotal > 0 ? String(((parseFloat(v) || 0) / 100 * advanceBaseTotal).toFixed(2)) : f.advance_amount,
    }));
  };
  const setAdvanceAmount = (v) => setForm(f => ({ ...f, advance_amount: v, advance_percent: '' }));

  return (
    <div>
      {!branchConfirmed && !isEdit && (
        <BranchSelectModal
          defaultBranchId={selectedBranchId || branchId}
          onConfirm={(id) => { setSelectedBranchId(id); setBranchConfirmed(true); }}
          onCancel={() => navigate(-1)}
        />
      )}
      {templatePickerFor && (
        <PackageOptionPickerModal
          templates={packageTemplates}
          onSelect={selectPackageTemplate}
          onStartFresh={() => setTemplatePickerFor(null)}
          onCancel={() => setTemplatePickerFor(null)}
        />
      )}
      <div className="page-header">
        <div><div className="page-title">{isEdit ? 'Edit Quotation' : 'New Quotation'}</div></div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <div>
            <label className="form-label">Branch</label>
            <select className="form-input" style={{ minWidth: 140 }} value={selectedBranchId} onChange={e => setSelectedBranchId(e.target.value)}>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      </div>

      {error && <div style={{ color: 'var(--danger)', marginBottom: 16, fontSize: 13 }}>{error}</div>}

      {/* Basic details */}
      <div className="card" style={{ padding: 24, marginBottom: 16 }}>
        <div className="form-grid">
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label className="form-label">Title / Package Name *</label>
            <input className="form-input" value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Bangkok Group Tour — July 2026" />
          </div>
          <div className="form-group">
            <label className="form-label">Quotation Type</label>
            <select className="form-input" value={form.quotation_type} onChange={e => handleTypeChange(e.target.value)}>
              <option value="simple">Simple</option>
              <option value="detail_package">Detail Package</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Destination</label>
            <input className="form-input" value={form.destination} onChange={e => set('destination', e.target.value)} placeholder="e.g. Bangkok, Thailand" />
          </div>
          <div className="form-group">
            <label className="form-label">Duration</label>
            <input className="form-input" value={form.duration} onChange={e => set('duration', e.target.value)} placeholder="e.g. 7 Nights / 8 Days" />
          </div>
          <div className="form-group">
            <label className="form-label">Currency</label>
            <select className="form-input" value={form.currency_code} onChange={e => set('currency_code', e.target.value)}>
              <option>LKR</option><option>USD</option><option>EUR</option><option>GBP</option><option>AED</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Valid Until</label>
            <input className="form-input" type="date" value={form.valid_until} onChange={e => set('valid_until', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Advance Required</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input className="form-input" type="number" value={form.advance_percent} onChange={e => setAdvancePercent(e.target.value)} placeholder="%" style={{ width: 90 }} />
              <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>% or</span>
              <input className="form-input" type="number" value={form.advance_amount} onChange={e => setAdvanceAmount(e.target.value)} placeholder="LKR amount" />
            </div>
            {form.advance_percent && advanceBaseTotal > 0 && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                {form.advance_percent}% of {advanceBaseTotal.toLocaleString()} = {((parseFloat(form.advance_percent) || 0) / 100 * advanceBaseTotal).toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-input" value={form.status} onChange={e => set('status', e.target.value)}>
              <option value="DRAFT">Draft</option>
              <option value="SENT">Sent</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Agent</label>
            <TypeSearch
              fetchFn={async (q) => {
                const { data } = await supabase.from('agents').select('id, tls_code, full_name').or(`full_name.ilike.%${q}%,tls_code.ilike.%${q}%`).limit(20);
                return (data || []).map(a => ({ ...a, label: `${a.tls_code} — ${a.full_name}` }));
              }}
              value={form.agent_ref_label}
              displayKey="label"
              placeholder="Which agent is sending this quote…"
              onSelect={a => { set('agent_ref_id', a.id); set('agent_ref_label', `${a.tls_code} — ${a.full_name}`); }}
              onClear={() => { set('agent_ref_id', ''); set('agent_ref_label', ''); }}
            />
          </div>
        </div>
      </div>

      {/* Simple: single rate + pax range */}
      {!isDetail && (
        <div className="card" style={{ padding: 24, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 14, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Pricing</div>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Per Pax Rate</label>
              <input className="form-input" type="number" value={form.per_pax_rate} onChange={e => set('per_pax_rate', e.target.value)} placeholder="0.00" />
            </div>
            <div className="form-group">
              <label className="form-label">Min Pax</label>
              <input className="form-input" type="number" value={form.min_pax} onChange={e => set('min_pax', e.target.value)} placeholder="e.g. 2" />
            </div>
            <div className="form-group">
              <label className="form-label">Max Pax</label>
              <input className="form-input" type="number" value={form.max_pax} onChange={e => set('max_pax', e.target.value)} placeholder="e.g. 20" />
            </div>
          </div>
        </div>
      )}

      {/* Simple mode inclusions — unchanged, flat free-text list */}
      {!isDetail && (
        <div className="card" style={{ padding: 24, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Inclusions</div>
            <button className="btn btn-sm btn-secondary" onClick={() => setInclusions(l => [...l, ''])}>+ Add Line</button>
          </div>
          {inclusions.map((inc, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
              <span style={{ color: '#c8a96e', fontSize: 14, minWidth: 20, fontWeight: 700 }}>✓</span>
              <input className="form-input" value={inc} onChange={e => setInclusions(l => l.map((x, idx) => idx === i ? e.target.value : x))}
                placeholder={`Inclusion ${i + 1}`} style={{ flex: 1 }} />
              {inclusions.length > 1 && (
                <button onClick={() => setInclusions(l => l.filter((_, idx) => idx !== i))}
                  style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 18 }}>×</button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Detail Package: one full block per option */}
      {isDetail && options.map((opt, optIdx) => (
        <div key={opt.key} style={{ border: '2px solid #c8a96e', borderRadius: 10, padding: 16, marginBottom: 24, background: 'rgba(200,169,110,0.04)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <input
              value={opt.label}
              onChange={e => updateOption(opt.key, { label: e.target.value })}
              style={{ fontWeight: 800, fontSize: 16, color: '#c8a96e', border: 'none', background: 'transparent', outline: 'none', padding: '4px 0' }}
            />
            {options.length > 1 && (
              <button className="btn btn-secondary btn-sm" onClick={() => removeOption(opt.key)}>Remove Option</button>
            )}
          </div>

          <div className="card" style={{ padding: 24, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 14, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Tour Details</div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr', gap: 16 }}>
              <div className="form-group">
                <label className="form-label">Travel Period</label>
                <input className="form-input" value={opt.detailMeta.travel_period}
                  onChange={e => updateOption(opt.key, { detailMeta: { ...opt.detailMeta, travel_period: e.target.value } })}
                  placeholder="e.g. 5th to 11th July 2026" />
              </div>
              <div className="form-group">
                <label className="form-label">Book Before</label>
                <input type="date" className="form-input" value={opt.detailMeta.book_before}
                  onChange={e => updateOption(opt.key, { detailMeta: { ...opt.detailMeta, book_before: e.target.value } })} />
              </div>
              <div className="form-group">
                <label className="form-label">Minimum Pax Note</label>
                <input className="form-input" value={opt.detailMeta.min_pax_note}
                  onChange={e => updateOption(opt.key, { detailMeta: { ...opt.detailMeta, min_pax_note: e.target.value } })}
                  placeholder="e.g. Prices based on minimum of 20 passengers" />
              </div>
            </div>
          </div>

          <div className="card" style={{ padding: 24, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Package Option Preset</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                {opt.templateInclusions.length || opt.templateExclusions.length
                  ? `Loaded — ${opt.templateInclusions.length} inclusions, ${opt.templateExclusions.length} exclusions to tick below`
                  : 'Optional — load a saved preset (e.g. "Malaysia Package") to pre-fill a checklist, or leave empty and build from scratch below'}
              </div>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={() => setTemplatePickerFor(opt.key)}>Load Package Option</button>
          </div>

          <div className="card" style={{ padding: 24, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 14, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Passenger Pricing</div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: 8, marginBottom: 6 }}>
              {['Pax Type', 'Rate Per Person', ''].map(h => (
                <div key={h} style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</div>
              ))}
            </div>
            {opt.paxPricing.map((p, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <input className="form-input" placeholder="e.g. Double Sharing" value={p.type}
                  onChange={e => updateOption(opt.key, { paxPricing: opt.paxPricing.map((r, idx) => idx === i ? { ...r, type: e.target.value } : r) })} />
                <input className="form-input" type="number" placeholder="0.00" min="0" step="0.01" value={p.rate}
                  onChange={e => updateOption(opt.key, { paxPricing: opt.paxPricing.map((r, idx) => idx === i ? { ...r, rate: e.target.value } : r) })} />
                <button className="btn btn-secondary btn-sm" disabled={opt.paxPricing.length === 1}
                  onClick={() => updateOption(opt.key, { paxPricing: opt.paxPricing.filter((_, idx) => idx !== i) })}>✕</button>
              </div>
            ))}
            <button className="btn btn-secondary btn-sm" onClick={() => updateOption(opt.key, { paxPricing: [...opt.paxPricing, { type: '', rate: '' }] })}>+ Add Row</button>
          </div>

          <div className="card" style={{ padding: 24, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 14 }}>Inclusions</div>
            {opt.templateInclusions.length > 0 && (
              <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>FROM PACKAGE OPTION — tick what applies</div>
                {opt.templateInclusions.map((it, i) => (
                  <label key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={it.checked} onChange={() => toggleTemplateItem(opt.key, 'templateInclusions', i)} />
                    <span style={{ fontSize: 13 }}>{it.label}</span>
                  </label>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>{opt.templateInclusions.length > 0 ? 'ADDITIONAL / CUSTOM' : ''}</div>
              <button className="btn btn-sm btn-secondary" onClick={() => updateOption(opt.key, { inclusions: [...opt.inclusions, ''] })}>+ Add Line</button>
            </div>
            {opt.inclusions.map((inc, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <span style={{ color: '#c8a96e', fontSize: 14, minWidth: 20, fontWeight: 700 }}>✓</span>
                <input className="form-input" value={inc}
                  onChange={e => updateOption(opt.key, { inclusions: opt.inclusions.map((x, idx) => idx === i ? e.target.value : x) })}
                  placeholder={`Inclusion ${i + 1}`} style={{ flex: 1 }} />
                {opt.inclusions.length > 1 && (
                  <button onClick={() => updateOption(opt.key, { inclusions: opt.inclusions.filter((_, idx) => idx !== i) })}
                    style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 18 }}>×</button>
                )}
              </div>
            ))}
          </div>

          <div className="card" style={{ padding: 24, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 14 }}>Exclusions</div>
            {opt.templateExclusions.length > 0 && (
              <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>FROM PACKAGE OPTION — tick what applies</div>
                {opt.templateExclusions.map((it, i) => (
                  <label key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={it.checked} onChange={() => toggleTemplateItem(opt.key, 'templateExclusions', i)} />
                    <span style={{ fontSize: 13 }}>{it.label}</span>
                  </label>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>{opt.templateExclusions.length > 0 ? 'ADDITIONAL / CUSTOM' : ''}</div>
              <button className="btn btn-sm btn-secondary" onClick={() => updateOption(opt.key, { exclusions: [...opt.exclusions, ''] })}>+ Add Line</button>
            </div>
            {opt.exclusions.map((exc, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <span style={{ color: 'var(--text-muted)', fontSize: 14, minWidth: 20, fontWeight: 700 }}>✕</span>
                <input className="form-input" value={exc}
                  onChange={e => updateOption(opt.key, { exclusions: opt.exclusions.map((x, idx) => idx === i ? e.target.value : x) })}
                  placeholder={`Exclusion ${i + 1}`} style={{ flex: 1 }} />
                {opt.exclusions.length > 1 && (
                  <button onClick={() => updateOption(opt.key, { exclusions: opt.exclusions.filter((_, idx) => idx !== i) })}
                    style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 18 }}>×</button>
                )}
              </div>
            ))}
          </div>

          <div className="card" style={{ padding: 24, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 14, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Flight Details</div>
            <div style={{ overflowX: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 0.9fr 1fr 0.7fr 0.7fr 0.7fr 0.7fr auto', gap: 8, marginBottom: 6, minWidth: 640 }}>
                {['Airline', 'Flight No', 'Date', 'From', 'To', 'Dep', 'Arr', ''].map(h => (
                  <div key={h} style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</div>
                ))}
              </div>
              {opt.flights.map((fl, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.4fr 0.9fr 1fr 0.7fr 0.7fr 0.7fr 0.7fr auto', gap: 8, marginBottom: 8, alignItems: 'center', minWidth: 640 }}>
                  <input className="form-input" placeholder="AirAsia" value={fl.airline}
                    onChange={e => updateOption(opt.key, { flights: opt.flights.map((f, idx) => idx === i ? { ...f, airline: e.target.value } : f) })} />
                  <input className="form-input" placeholder="FD141" value={fl.flight_no}
                    onChange={e => updateOption(opt.key, { flights: opt.flights.map((f, idx) => idx === i ? { ...f, flight_no: e.target.value } : f) })} />
                  <input className="form-input" placeholder="04 Jul 2026" value={fl.date}
                    onChange={e => updateOption(opt.key, { flights: opt.flights.map((f, idx) => idx === i ? { ...f, date: e.target.value } : f) })} />
                  <input className="form-input" placeholder="CMB" value={fl.from}
                    onChange={e => updateOption(opt.key, { flights: opt.flights.map((f, idx) => idx === i ? { ...f, from: e.target.value } : f) })} />
                  <input className="form-input" placeholder="DMK" value={fl.to}
                    onChange={e => updateOption(opt.key, { flights: opt.flights.map((f, idx) => idx === i ? { ...f, to: e.target.value } : f) })} />
                  <input className="form-input" placeholder="23:15" value={fl.dep}
                    onChange={e => updateOption(opt.key, { flights: opt.flights.map((f, idx) => idx === i ? { ...f, dep: e.target.value } : f) })} />
                  <input className="form-input" placeholder="04:20" value={fl.arr}
                    onChange={e => updateOption(opt.key, { flights: opt.flights.map((f, idx) => idx === i ? { ...f, arr: e.target.value } : f) })} />
                  <button className="btn btn-secondary btn-sm" disabled={opt.flights.length === 1}
                    onClick={() => updateOption(opt.key, { flights: opt.flights.filter((_, idx) => idx !== i) })}>✕</button>
                </div>
              ))}
            </div>
            <button className="btn btn-secondary btn-sm" style={{ marginTop: 4 }}
              onClick={() => updateOption(opt.key, { flights: [...opt.flights, { ...EMPTY_FLIGHT }] })}>+ Add Flight Segment</button>
          </div>

          <div className="card" style={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Day-by-Day Itinerary</div>
              <button className="btn btn-secondary btn-sm" onClick={() => updateOption(opt.key, { itinerary: [...opt.itinerary, EMPTY_DAY(opt.itinerary.length + 1)] })}>+ Add Day</button>
            </div>
            {opt.itinerary.map((day, i) => (
              <div key={i} style={{ border: '1px solid var(--border)', borderLeft: '3px solid #c8a96e', borderRadius: '0 6px 6px 0', padding: 16, marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ fontWeight: 700, color: '#c8a96e', fontSize: 13 }}>Day {day.day}</div>
                  {opt.itinerary.length > 1 && (
                    <button className="btn btn-secondary btn-sm"
                      onClick={() => updateOption(opt.key, { itinerary: opt.itinerary.filter((_, idx) => idx !== i).map((d, idx) => ({ ...d, day: idx + 1 })) })}>
                      Remove
                    </button>
                  )}
                </div>
                <div className="form-group" style={{ marginBottom: 10 }}>
                  <label className="form-label" style={{ fontSize: 11 }}>Day Title</label>
                  <input className="form-input" placeholder="e.g. Arrival in Bangkok – Transfer to Pattaya"
                    value={day.title}
                    onChange={e => updateOption(opt.key, { itinerary: opt.itinerary.map((d, idx) => idx === i ? { ...d, title: e.target.value } : d) })} />
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ fontSize: 11 }}>
                    Activities <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(one per line)</span>
                  </label>
                  <textarea className="form-input" rows={4}
                    placeholder={"Breakfast at hotel\nVisit Coral Island by Speed Boat\nIndian Lunch included"}
                    value={day.activities}
                    onChange={e => updateOption(opt.key, { itinerary: opt.itinerary.map((d, idx) => idx === i ? { ...d, activities: e.target.value } : d) })} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {isDetail && (
        <button className="btn btn-secondary" style={{ marginBottom: 16 }} onClick={addOption}>+ Add Option</button>
      )}

      {/* Notes */}
      <div className="card" style={{ padding: 24, marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 14, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Notes / Terms</div>
        <textarea className="form-textarea" rows={4} value={form.notes} onChange={e => set('notes', e.target.value)}
          placeholder="Rates are per person based on twin sharing. Prices subject to change. Visa not included…" />
      </div>
    </div>
  );
}
